import { toast } from "sonner";
import { Bell, Globe2, Users2, ArrowRightLeft, Compass } from "lucide-react";
import { useApp } from "@/store/AppContext";
import { useAuth } from "@/store/AuthContext";
import {
  useGroup,
  useGroupMembers,
  useMyGroup,
  useMySegmentation,
  usePatchSegmentation,
  useMatchCandidates,
  useSwitchGroup,
} from "@/lib/hooks";
import { SEGMENTATION_STEPS } from "@/lib/segmentation";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Community() {
  const { notifications, setNotifications, sendNudge, authed } = useApp();
  const { user, login } = useAuth();
  const { data: publicGroup } = useGroup();
  const { data: publicMembers = [] } = useGroupMembers();
  const { data: myGroupData } = useMyGroup();
  const { data: segmentation } = useMySegmentation();
  const patchSeg = usePatchSegmentation();
  const { data: candidates = [] } = useMatchCandidates();
  const switchMut = useSwitchGroup();

  const group = myGroupData || publicGroup;
  const members = myGroupData?.members || publicMembers;
  const myMembership = myGroupData?.myMembership;

  const toggleNotif = (key) => (v) => {
    setNotifications({ ...notifications, [key]: v });
  };

  const meCard = authed && myMembership
    ? {
        id: user.user_id, name: myMembership.name, picture: myMembership.picture,
        timezone: myMembership.timezone, ageBand: myMembership.ageBand,
        overlapPct: myMembership.overlapPct, color: "#292524",
      }
    : null;

  const combined = meCard ? [meCard, ...members] : members;

  // 2nd and 3rd best circles (exclude the current active one)
  const activeId = group?.id;
  const otherCircles = candidates.filter((c) => c.id !== activeId).slice(0, 3);

  const handleSignalChange = (field) => (value) => {
    if (!authed) {
      toast("Sign in to save", { action: { label: "Sign in", onClick: () => login() } });
      return;
    }
    patchSeg.mutate({ [field]: value }, {
      onSuccess: () => toast("Signal updated", { description: `${field} → ${value}. Recomputing match…` }),
      onError: (e) => toast.error("Couldn't save", { description: e?.response?.data?.detail || e.message }),
    });
  };

  const handleSwitch = (candidate) => {
    if (!authed) {
      toast("Sign in to switch", { action: { label: "Sign in", onClick: () => login() } });
      return;
    }
    switchMut.mutate(candidate.id, {
      onSuccess: (data) => toast("Switched circle", { description: `${data.group_name} · ${data.match_score}% match` }),
      onError: (e) => toast.error("Switch failed", { description: e?.response?.data?.detail || e.message }),
    });
  };

  return (
    <div className="space-y-10" data-testid="community-page">
      <header className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-8">
          <span className="eyebrow">Your matched circle</span>
          <h1 className="font-serif text-4xl sm:text-5xl text-stone-900 mt-2 leading-tight">
            {group?.name || "Your circle"}
          </h1>
          <p className="text-stone-600 mt-3 max-w-xl leading-relaxed">{group?.tagline}</p>
          <div className="flex flex-wrap gap-2 mt-4">
            {(group?.segments?.ethics || []).map((e) => (
              <span key={e} className="tag-pill">{e}</span>
            ))}
          </div>
        </div>
        <div className="md:col-span-4 card-editorial p-6 space-y-4" data-testid="match-stats">
          <div className="flex items-center justify-between">
            <span className="eyebrow">Match score</span>
            <span className="data-mono text-2xl text-terracotta">{myMembership?.matchScore ?? group?.matchScore ?? "—"}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-stone-100 overflow-hidden">
            <div className="h-full bg-terracotta" style={{ width: `${myMembership?.matchScore ?? group?.matchScore ?? 0}%` }} />
          </div>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div><p className="eyebrow">Age band</p><p className="data-mono">{group?.segments?.ageBand || "—"}</p></div>
            <div><p className="eyebrow">Timezones</p><p className="data-mono">{group?.segments?.timezones?.length ?? "—"}</p></div>
            <div><p className="eyebrow">Languages</p><p className="data-mono">{(group?.segments?.languages || []).join(" · ")}</p></div>
            <div><p className="eyebrow">Ethos</p><p className="data-mono">{group?.segments?.religion || "—"}</p></div>
          </div>
        </div>
      </header>

      {/* ---------- Your signals editor ---------- */}
      <section className="card-editorial p-6 md:p-8" data-testid="signals-editor">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <span className="eyebrow">Your signals</span>
            <h2 className="font-serif text-3xl text-stone-900 mt-1">What we pair you on</h2>
          </div>
          <p className="text-xs text-stone-500 max-w-xs">Change any signal and your circle re-tunes overnight.</p>
        </div>
        {!authed ? (
          <p className="text-sm text-stone-500 mt-4">
            <button onClick={login} className="underline text-terracotta">Sign in</button> to edit your signals.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mt-6">
            {SEGMENTATION_STEPS.map((step) => {
              const current = segmentation?.[step.id] || "";
              return (
                <div key={step.id} className="flex items-center justify-between gap-4 py-2 border-b border-stone-100 last:border-b-0">
                  <span className="eyebrow flex-shrink-0">{step.label}</span>
                  <Select
                    value={current}
                    onValueChange={handleSignalChange(step.id)}
                    disabled={patchSeg.isPending}
                  >
                    <SelectTrigger
                      className="w-56 h-9 text-sm"
                      data-testid={`signal-trigger-${step.id}`}
                    >
                      <SelectValue placeholder={`Choose ${step.label.toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {step.options.map((o) => (
                        <SelectItem key={o} value={o} data-testid={`signal-opt-${step.id}-${o}`}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ---------- Members ---------- */}
      <section className="space-y-5">
        <div className="flex items-baseline justify-between">
          <h2 className="font-serif text-3xl text-stone-900 flex items-center gap-3">
            <Users2 size={22} /> Members
          </h2>
          <span className="eyebrow">{group?.memberCount ?? combined.length} keeping habits together</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="members-grid">
          {combined.map((m) => {
            const isMe = meCard && m.id === meCard.id;
            const avatar = m.picture || m.avatar;
            return (
              <div key={m.id} className="card-editorial p-5 flex items-center gap-4" data-testid={`member-${m.id}`}>
                {avatar ? (
                  <img src={avatar} alt={m.name} className="w-14 h-14 rounded-full object-cover border border-stone-200" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-charcoal text-paper flex items-center justify-center font-serif text-xl">
                    {m.name?.[0] || "·"}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-serif text-xl text-stone-900 leading-tight truncate">{m.name}</p>
                    {isMe && <span className="tag-pill">you</span>}
                  </div>
                  <p className="text-xs text-stone-500 mt-0.5 flex items-center gap-1.5">
                    <Globe2 size={12} /> {m.timezone} · {m.ageBand}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="h-1 flex-1 rounded-full bg-stone-100 overflow-hidden">
                      <div className="h-full" style={{ width: `${m.overlapPct}%`, background: m.color || "#292524" }} />
                    </div>
                    <span className="data-mono text-xs">{m.overlapPct}%</span>
                  </div>
                </div>
                {!isMe && (
                  <button
                    className="btn-outline"
                    onClick={() => sendNudge({ recipient_id: m.id, recipient_name: m.name })}
                    data-testid={`member-nudge-${m.id}`}
                  >
                    <Bell size={14} /> Nudge
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ---------- Other circles ---------- */}
      {authed && otherCircles.length > 0 && (
        <section className="space-y-5" data-testid="close-matches">
          <div>
            <h2 className="font-serif text-3xl text-stone-900 flex items-center gap-3">
              <Compass size={22} /> Circles you&apos;re close to
            </h2>
            <p className="text-sm text-stone-600 mt-1">Cadence would also welcome you here. Try one for a week.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {otherCircles.slice(0, 2).map((c) => (
              <article key={c.id} className="card-editorial p-6 flex flex-col gap-4" data-testid={`candidate-${c.id}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <h3 className="font-serif text-2xl text-stone-900 leading-tight">{c.name}</h3>
                  <span className="data-mono text-terracotta text-lg">{c.match_score}%</span>
                </div>
                <p className="text-sm text-stone-600">{c.tagline}</p>
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {(c.cover_members || []).slice(0, 4).map((m) => (
                      <img key={m.id} src={m.avatar} alt={m.name} className="w-7 h-7 rounded-full object-cover border-2 border-white" />
                    ))}
                  </div>
                  <span className="eyebrow">{c.segments?.ageBand} · {(c.segments?.languages || []).join(" · ")}</span>
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
                  <div className="flex-1 text-[10px] text-stone-500">
                    habit overlap {Math.round(c.habit_overlap * 100)}% · signal fit {Math.round(c.segmentation_score * 100)}%
                  </div>
                  <button
                    className="btn-accent"
                    onClick={() => handleSwitch(c)}
                    disabled={switchMut.isPending}
                    data-testid={`switch-to-${c.id}`}
                  >
                    <ArrowRightLeft size={14} /> Switch
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* ---------- Notifications ---------- */}
      <section className="card-editorial p-6 md:p-8" data-testid="notifications-panel">
        <span className="eyebrow">Calendar subscriptions</span>
        <h2 className="font-serif text-3xl text-stone-900 mt-1">What Cadence may whisper</h2>
        <p className="text-sm text-stone-600 mt-2 max-w-lg">
          You&apos;ll only hear from the circle when the whole circle has agreed. Toggle what you&apos;d like to receive.
        </p>
        {!authed && (
          <p className="text-xs text-stone-500 mt-3">
            <button onClick={login} className="underline text-terracotta">Sign in</button> to save your notification choices.
          </p>
        )}
        <div className="mt-6 divide-y divide-stone-100">
          {[
            { key: "calendarPush", title: "Shared calendar reminders", body: "5 minutes before any shared habit begins." },
            { key: "peerNudges", title: "Peer nudges", body: "A soft pulse when a member sends you one." },
            { key: "weeklyDigest", title: "Weekly digest", body: "Sunday morning · how the circle kept the week." },
          ].map((row) => (
            <div key={row.key} className="py-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-stone-900 text-sm font-medium">{row.title}</p>
                <p className="text-xs text-stone-500 mt-0.5">{row.body}</p>
              </div>
              <Switch
                checked={!!notifications[row.key]}
                onCheckedChange={toggleNotif(row.key)}
                data-testid={`notif-${row.key}`}
              />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
